import { FC } from 'react';
import { Link } from 'ui/links';
import styled from 'styled-components';
import { TABLE_GRID } from 'modules/savedViewsIndex/constants';
import { useSelectionContext } from 'modules/savedViewsIndex/context';
import { ListedView } from 'modules/savedViewsIndex/types';
import { FavoriteOutlined, Lock16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { CHECKBOX_STATUS } from 'ui/checkbox/checkboxStatus';
import { CheckboxGridCell } from 'ui/indexPages/tables/cells';
import { LastModifiedCell } from 'ui/indexPages/tables/cells/LastModifiedCell';
import { GridRow } from 'ui/indexPages/tables/grid';
import { body2, caption, colors, spacing } from 'ui/theme';
import { ViewOverflowMenu } from './ViewOverflowMenu';

const Cell = styled.div`
  display: flex;
  ${body2};
  justify-content: center;
`;

const StyledLink = styled(Link)`
  text-decoration-color: transparent;
  font-weight: 400;
  color: ${colors.textPrimary};
`;

const DescriptionCell = styled(Cell)`
  ${caption};
  color: ${colors.textSecondary};
  flex-direction: column;
  overflow-wrap: break-word;
`;

const NameLabel = styled.div`
  ${caption};
  align-items: center;
  color: ${colors.grey50};
  display: flex;
  margin-top: ${spacing.eighth};
  > svg {
    margin-right: ${spacing.quarter};
  }
  > span {
    border-bottom: ${spacing.single} dashed ${colors.grey50};
    text-decoration: none;
  }
`;

const NameCell = styled(Cell)`
  flex-direction: column;
  overflow-wrap: break-word;
`;

type Props = {
  view: ListedView;
};

export const ViewRow: FC<React.PropsWithChildren<Props>> = ({ view }) => {
  const {
    selected: selectedIds,
    toggleSelection,
    deleteDataByViewId,
    setDeleteDataByViewId,
  } = useSelectionContext();
  const {
    id,
    name,
    description,
    isPrivate,
    isProjectDefault,
    createdAt,
    createdByEmail,
    createdByFirstName,
    createdByLastName,
    updatedAt,
    updatedByEmail,
    updatedByFirstName,
    updatedByLastName,
    updatedByAvatarUrl,
  } = view;

  const checkboxStatus = selectedIds.includes(id)
    ? CHECKBOX_STATUS.CHECKED
    : CHECKBOX_STATUS.UNCHECKED;

  const createdBy = {
    emailAddress: createdByEmail,
    fullName: `${createdByFirstName} ${createdByLastName}`,
  };

  const modifiedBy = {
    emailAddress: updatedByEmail,
    fullName: `${updatedByFirstName} ${updatedByLastName}`,
    avatarUrl: updatedByAvatarUrl,
  };

  const handleClick = () => {
    toggleSelection(id);
    const previouslySelected = Object.keys(deleteDataByViewId).some((i) => parseInt(i, 10) === id);
    if (!previouslySelected) {
      setDeleteDataByViewId({
        ...deleteDataByViewId,
        [id]: { id, name, createdByEmail, isPrivate },
      });
    }
  };

  return (
    <GridRow gridLayout={TABLE_GRID}>
      <CheckboxGridCell handleClick={handleClick} id={id} status={checkboxStatus} />
      <NameCell>
        <StyledLink href={`/messagingInsights/views/${id}`}>{name}</StyledLink>
        {isPrivate && !isProjectDefault && (
          <NameLabel>
            <Lock16 height={12} width={12} color={colors.iconPrimary} />
            <Tooltip tooltipContent={'Only you have access'}>
              <span>Only me</span>
            </Tooltip>
          </NameLabel>
        )}
        {isProjectDefault && (
          <NameLabel>
            <FavoriteOutlined height={12} width={12} color={colors.iconPrimary} />
            <Tooltip tooltipContent={'Default view for this project'}>
              <span>Default</span>
            </Tooltip>
          </NameLabel>
        )}
      </NameCell>
      <DescriptionCell>{description}</DescriptionCell>

      <LastModifiedCell
        data-test="view-last-modified-cell"
        createdBy={createdBy}
        createdAt={createdAt}
        modifiedBy={modifiedBy}
        modifiedAt={updatedAt}
      />
      <ViewOverflowMenu view={view} />
    </GridRow>
  );
};
